import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Calendar } from 'lucide-react';
import PostImage from './PostImage';

/**
 * Liquid Glass editorial mosaic.
 * Asymmetric bento with rounded-3xl frosted-glass surrounds, refractive borders
 * and gradient meta chips. The image fills, the metadata floats inside a glass
 * strip at the bottom — Apple visionOS card vocabulary.
 */
const PostsMosaic = ({ posts = [] }) => {
  const filtered = (posts || []).filter((p) => p && p.category !== 'announcements');
  if (!filtered.length) return null;

  const sorted = [...filtered].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const [hero, ...rest] = sorted;
  const mediums = rest.slice(0, 2);
  const smalls = rest.slice(2, 5);

  const Card = ({ post, size }) => {
    const isHero = size === 'hero';
    const isMed = size === 'med';
    return (
      <Link
        to={`/news/${post.slug}`}
        className="group relative block h-full overflow-hidden rounded-[1.75rem] ring-1 ring-white/40 shadow-xl shadow-slate-900/10 hover:shadow-sky-500/15 hover:ring-white/70 transition-all duration-500 hover:-translate-y-1"
        data-testid={`mosaic-card-${post.slug}`}
      >
        <PostImage
          src={post.featured_image}
          seed={post.slug || post.id}
          alt={post.title}
          loading={isHero ? 'eager' : 'lazy'}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[900ms] ease-out"
        />
        {/* Sky-tinted gradient base */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/30 to-transparent" />

        {/* Featured ribbon */}
        {post.featured && (
          <span className="absolute top-4 left-4 glass-strong rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-orange-600">★ Featured</span>
        )}
        {/* Category chip top-right */}
        <span className="absolute top-4 right-4 glass-strong rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-sky-700 capitalize">
          {post.category}
        </span>

        {/* Glass info strip at bottom */}
        <div className={`absolute inset-x-3 bottom-3 glass-dark rounded-2xl ${isHero ? 'p-5 md:p-6' : 'p-4'}`}>
          <h3
            className={`text-white font-bold tracking-[-0.02em] leading-[1.15] line-clamp-3 group-hover:text-sky-200 transition-colors ${
              isHero ? 'text-2xl md:text-3xl' : isMed ? 'text-lg md:text-xl' : 'text-sm md:text-base'
            }`}
          >
            {post.title}
          </h3>
          {(isHero || isMed) && post.excerpt && (
            <p className={`text-white/75 mt-2 line-clamp-2 leading-relaxed ${isHero ? 'text-sm md:text-base' : 'text-xs md:text-sm'}`}>
              {post.excerpt}
            </p>
          )}
          <div className="flex items-center justify-between mt-3 text-[10px] uppercase tracking-widest font-semibold text-white/60">
            <span className="flex items-center gap-1.5">
              <Calendar size={10} />
              {new Date(post.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <ArrowUpRight size={14} className="text-sky-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </div>
      </Link>
    );
  };

  return (
    <section className="relative py-24" data-testid="posts-mosaic">
      <div className="aurora-orb w-[420px] h-[420px] -top-20 right-1/4 bg-fuchsia-200/40" />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Section masthead */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-sky-600 mb-3">Latest updates</p>
            <h2 className="text-5xl md:text-6xl font-bold tracking-[-0.04em] text-slate-900 leading-none">
              Latest from <span className="font-serif-accent italic font-light bg-gradient-to-r from-sky-500 to-indigo-500 bg-clip-text text-transparent">Paisley</span>
            </h2>
          </div>
          <Link
            to="/news"
            className="hidden sm:inline-flex items-center gap-2 glass-strong glass-hover rounded-full px-5 py-2.5 text-sm font-semibold text-slate-800"
          >
            All news
            <ArrowUpRight size={15} />
          </Link>
        </div>

        {/* Bento */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="mosaic-grid">
          <div className="md:col-span-2 md:row-span-2 h-[440px] md:h-auto md:min-h-[640px]">
            <Card post={hero} size="hero" />
          </div>
          {mediums.map((p) => (
            <div key={p.id} className="h-[260px] md:h-auto md:min-h-[312px]">
              <Card post={p} size="med" />
            </div>
          ))}
          {smalls.map((p) => (
            <div key={p.id} className="h-[220px]">
              <Card post={p} size="sm" />
            </div>
          ))}
        </div>

        <div className="sm:hidden mt-8 text-center">
          <Link to="/news" className="font-semibold text-sky-600">All news →</Link>
        </div>
      </div>
    </section>
  );
};

export default PostsMosaic;
